import { AttachMoney, Edit, Save } from '@mui/icons-material';
import { Box, Card, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import styled from 'styled-components';
import { UserContext } from '@/shared/contexts/UserContext';
import { useApi } from '@shared/services/api';

const StyledCard = styled(Card)`
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.02) 100%);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(76, 175, 80, 0.4);
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.1);
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.25) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2e7d32;
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RateSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

interface HourlyRateBannerProps {
  onUpdate?: () => void;
}

export function HourlyRateBanner({ onUpdate }: HourlyRateBannerProps) {
  const { user, updateUser } = useContext(UserContext);
  const api = useApi();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<string>(user?.hourlyRate?.toString() || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!user?.id) return;

    const hourlyRate = editValue ? parseFloat(editValue) : undefined;

    // Validate rate
    if (editValue && (isNaN(hourlyRate!) || hourlyRate! < 0)) {
      return;
    }

    setIsSaving(true);
    try {
      const response = await api.put('user', {
        id: user.id,
        hourlyRate,
      });

      if (!response.error) {
        // Update user context
        updateUser({ ...user, hourlyRate });
        setIsEditing(false);
        onUpdate?.();
      }
    } catch (error) {
      console.error('Error updating hourly rate:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(user?.hourlyRate?.toString() || '');
    setIsEditing(false);
  };

  return (
    <StyledCard>
      <LeftSection>
        <IconWrapper>
          <AttachMoney sx={{ fontSize: 28 }} />
        </IconWrapper>
        <TextSection>
          <Typography variant="subtitle2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
            Consulting Rate
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Set your hourly consulting rate for potential engagements
          </Typography>
        </TextSection>
      </LeftSection>

      <RateSection>
        {isEditing ? (
          <>
            <TextField
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              type="number"
              size="small"
              placeholder="0"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                endAdornment: <InputAdornment position="end">/hr</InputAdornment>,
              }}
              sx={{
                width: '140px',
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#4caf50',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2e7d32',
                  },
                },
              }}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') handleCancel();
              }}
            />
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Save (Enter)">
                <IconButton
                  size="small"
                  onClick={handleSave}
                  disabled={isSaving}
                  sx={{
                    'color': '#2e7d32',
                    '&:hover': {
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    },
                  }}
                >
                  <Save fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel (Esc)">
                <IconButton
                  size="small"
                  onClick={handleCancel}
                  disabled={isSaving}
                  sx={{
                    'color': 'text.secondary',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '18px' }}>×</Typography>
                </IconButton>
              </Tooltip>
            </Box>
          </>
        ) : (
          <>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: user?.hourlyRate ? '#2e7d32' : 'text.disabled',
                minWidth: '100px',
                textAlign: 'right',
              }}
            >
              {user?.hourlyRate ? `$${user.hourlyRate}/hr` : 'Not set'}
            </Typography>
            <Tooltip title="Edit rate">
              <IconButton
                size="small"
                onClick={() => setIsEditing(true)}
                sx={{
                  'color': 'text.secondary',
                  '&:hover': {
                    color: '#2e7d32',
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  },
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </RateSection>
    </StyledCard>
  );
}
